import { createListenerMiddleware } from '@reduxjs/toolkit'
import { AppDispatch, RootState } from '@/store/store'
import { MattersActions } from './slice'
import { getMatterList } from '../fetch/get-matter-list'
import { kMatterListCacheDuration, kSessionStorageActiveMatterIdKey } from '@/matter/constants'
import { getAuth } from 'firebase/auth'
import { Userpilot } from 'userpilot'
import { CollaborationActions } from '@/collaboration/store/slice'
import { AgentConversationsActions } from '@/agent/conversations/store/slice'
import { DraftingActions } from '@/drafting/store/slice'
import type { MatterMemberRead } from '@/matter/components/manage-members/schemas'
import { AnalyticsEvent } from '@/analytics/schema/events.schema'
import { getTrackEventGlobal } from '@/analytics/utils/analytics-helpers'
import { SetActiveMatterSource } from '@/matter/enums/set-active-matter-source.enums'

export const mattersListenerMiddleware = createListenerMiddleware()
const startListener = mattersListenerMiddleware.startListening.withTypes<RootState, AppDispatch>()

/**
 * Refresh Matter List Action Listener
 *
 * This listener fetches the matters from the server.
 * It checks the cache duration and only fetches if the cache has expired, or if we are forcing a refresh.
 */
startListener({
  actionCreator: MattersActions.refreshMatterList,
  effect: async (action, listenerApi) => {
    const { forceRefresh } = action.payload

    // Get the existing state
    const state = listenerApi.getState().mattersState

    // Cache Handling
    const cacheDuration = kMatterListCacheDuration
    const lastFetch = state.lastRefresh
    const cacheExpired = lastFetch === null || Date.now() - lastFetch > cacheDuration

    // Skip a non-forced refresh while the cache is still fresh or a fetch is already running.
    // A forced refresh always runs, even mid-poll; the request id guard below drops whichever
    // response lands older, so a forced refresh can't be masked by an in-flight one.
    if (!forceRefresh && (!cacheExpired || state.loading)) {
      return
    }

    // Claim this fetch as the latest; a response tagged with an older id is dropped below.
    const requestId = state.requestId + 1
    listenerApi.dispatch(MattersActions.beginMatterListRefresh(requestId))

    try {
      // Fetch matters
      const response = await getMatterList()

      // Drop the response if a newer refresh has since superseded this one
      if (listenerApi.getState().mattersState.requestId !== requestId) return

      // Populate the matters into state
      listenerApi.dispatch(MattersActions.setMatters(response.matters))

      // Set initial load complete
      listenerApi.dispatch(MattersActions.setInitialLoadComplete(true))

      // Set loading to false and record the refresh time
      listenerApi.dispatch(MattersActions.setLoading(false))
      listenerApi.dispatch(MattersActions.setLastRefresh(Date.now()))
    } catch {
      // Drop the failure if a newer refresh has since superseded this one
      if (listenerApi.getState().mattersState.requestId !== requestId) return

      listenerApi.dispatch(MattersActions.setError(true))
      listenerApi.dispatch(MattersActions.setLoading(false))
      listenerApi.dispatch(MattersActions.setLastRefresh(Date.now()))
    }
  },
})

/**
 * Set Active Matter Id Action Listener
 *
 * Persists the active matter id to sessionStorage to support restoring the selected matter
 * across page refreshes.
 */
startListener({
  actionCreator: MattersActions.setActiveMatterId,
  effect: ({ payload: { matterId, source } }, listenerApi) => {
    const previousMatterId = listenerApi.getOriginalState().mattersState.activeMatterId
    const storedActiveMatterId = sessionStorage.getItem(kSessionStorageActiveMatterIdKey)

    // Remove from storage if the matter id is null
    if (matterId === null) {
      sessionStorage.removeItem(kSessionStorageActiveMatterIdKey)
    }
    // Set the new matter id in storage if it is different from the stored active matter id
    else if (matterId !== storedActiveMatterId) {
      sessionStorage.setItem(kSessionStorageActiveMatterIdKey, matterId)
    }

    // Sync active matter ID to Userpilot so checklist URLs can use {activeMatterId}
    const userId = getAuth().currentUser?.uid
    if (userId && matterId && matterId !== storedActiveMatterId) {
      Userpilot.identify(userId, { activeMatterId: matterId })
    }

    const isSessionRestore = source === SetActiveMatterSource.SessionRestore
    if (matterId && matterId !== previousMatterId && !isSessionRestore) {
      // MatterSelectionChanged = "matter context entered" (user navigated or chose a matter), not silent
      // sessionStorage rehydration. activeMatterId duplicates selectedMatterId here on purpose: trackEvent's
      // global activeMatterId is stale on this synchronous dispatch, so we override it back to matterId.
      getTrackEventGlobal()?.(AnalyticsEvent.MatterSelectionChanged, { selectedMatterId: matterId, activeMatterId: matterId })
    }
  },
})

// A stable fingerprint of a matter's accepted members, for detecting when that set changes between reloads.
const acceptedMemberKey = (members: MatterMemberRead[]) =>
  members
    .filter((member) => member.accepted_at)
    .map((member) => member.user_id)
    .sort()
    .join(',')

/**
 * Refresh Matter Content On Membership Change Listener
 *
 * When a matter's accepted-member set changes on a members reload (e.g. an invite is accepted, or a
 * member is removed), refresh the matter's membership-filtered content, since the accepted-member set
 * determines which members' content is visible.
 */
startListener({
  actionCreator: CollaborationActions.fetchMembersSucceeded,
  effect: (action, listenerApi) => {
    const { matterId } = action.payload
    // Only act on a reload, not the initial members load: on matter entry the content is already
    // fetched fresh, so no need to react.
    const beforeEntry = listenerApi.getOriginalState().collaborationState.membersByMatterId[matterId]
    if (!beforeEntry?.initialLoadComplete) return

    const after = listenerApi.getState().collaborationState.membersByMatterId[matterId]?.members ?? []
    if (acceptedMemberKey(beforeEntry.members) === acceptedMemberKey(after)) return

    listenerApi.dispatch(AgentConversationsActions.refreshMatterConversationList({ matterId, forceRefresh: true }))
    listenerApi.dispatch(DraftingActions.invalidateMatterDrafts({ matterId }))
  },
})
